/**
 * <mb-galeria>: comportamiento de snippets/mb-galeria.liquid (compartido por la ficha del tarro y la de granel).
 * Miniaturas, contador "n / total" y teclado sobre la pista con scroll-snap. Sin JavaScript la pista sigue deslizándose.
 */

const reducirMovimiento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class MbGaleria extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.#galeria();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  get #signal() {
    return /** @type {AbortController} */ (this.#controller).signal;
  }


  #galeria() {
    const pista = this.querySelector('[data-pista]');
    if (!(pista instanceof HTMLElement)) return;
    const miniaturas = [...this.querySelectorAll('[data-miniatura]')];
    const contador = this.querySelector('[data-contador]');
    const total = pista.children.length;
    let actual = 0;
    let pendiente = 0;
    let bloqueo = 0;

    /** @param {number} indice */
    const marcar = (indice) => {
      if (indice === actual && miniaturas[indice]?.getAttribute('aria-current') === 'true') return;
      actual = indice;
      miniaturas.forEach((miniatura, n) => {
        if (n === indice) miniatura.setAttribute('aria-current', 'true');
        else miniatura.removeAttribute('aria-current');
      });
      if (contador) contador.textContent = `${indice + 1} / ${total}`;
      const activa = miniaturas[indice];
      const contenedor = activa?.parentElement;
      if (activa instanceof HTMLElement && contenedor) {
        // Desplaza solo la fila de miniaturas, nunca la página
        contenedor.scrollTo({
          left: activa.offsetLeft - (contenedor.clientWidth - activa.offsetWidth) / 2,
          top: activa.offsetTop - (contenedor.clientHeight - activa.offsetHeight) / 2,
          behavior: reducirMovimiento() ? 'auto' : 'smooth',
        });
      }
    };

    /** @param {number} indice */
    const ir = (indice) => {
      const destino = Math.max(0, Math.min(total - 1, indice));
      // Mientras dura el desplazamiento programado no se recalcula la diapositiva (evita parpadeos del contador)
      clearTimeout(bloqueo);
      bloqueo = window.setTimeout(() => { bloqueo = 0; }, 700);
      pista.scrollTo({ left: destino * pista.clientWidth, behavior: reducirMovimiento() ? 'auto' : 'smooth' });
      marcar(destino);
    };

    pista.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(pendiente);
        pendiente = requestAnimationFrame(() => {
          if (pista.clientWidth === 0 || bloqueo) return;
          marcar(Math.round(pista.scrollLeft / pista.clientWidth));
        });
      },
      { passive: true, signal: this.#signal }
    );

    pista.addEventListener(
      'scrollend',
      () => {
        clearTimeout(bloqueo);
        bloqueo = 0;
        if (pista.clientWidth > 0) marcar(Math.round(pista.scrollLeft / pista.clientWidth));
      },
      { passive: true, signal: this.#signal }
    );

    // Al cambiar de variante (tamaño, sabor…), saltar a su foto si la tiene (evento `mb:variante` de la ficha).
    /** @param {number} mediaId */
    const irAFoto = (mediaId) => {
      if (!mediaId) return;
      const indice = [...pista.children].findIndex((d) => d instanceof HTMLElement && d.dataset.mediaId === String(mediaId));
      if (indice >= 0 && indice !== actual) ir(indice);
    };
    // La primera variante anunciada es la de carga: no se salta (la ficha del tarro abre con la vista previa del nombre).
    // Solo cuando el cliente elige otra variante se muestra su foto.
    let ultimaVariante = /** @type {any} */ (window).mbVariante?.variante_id ?? null;
    document.addEventListener(
      'mb:variante',
      (evento) => {
        const detalle = /** @type {CustomEvent} */ (evento).detail ?? {};
        if (ultimaVariante === null) { ultimaVariante = detalle.variante_id; return; }
        if (detalle.variante_id === ultimaVariante) return;
        ultimaVariante = detalle.variante_id;
        irAFoto(Number(detalle.media_id));
      },
      { signal: this.#signal }
    );

    miniaturas.forEach((miniatura, n) => {
      miniatura.addEventListener('click', () => ir(n), { signal: this.#signal });
    });

    pista.addEventListener(
      'keydown',
      (evento) => {
        if (evento.key === 'ArrowRight') ir(actual + 1);
        else if (evento.key === 'ArrowLeft') ir(actual - 1);
        else if (evento.key === 'Home') ir(0);
        else if (evento.key === 'End') ir(total - 1);
        else return;
        evento.preventDefault();
      },
      { signal: this.#signal }
    );
  }
}

if (!customElements.get('mb-galeria')) {
  customElements.define('mb-galeria', MbGaleria);
}
